import { AssetProxy } from '$lib/services/api/asset-proxy';
import { getImmutable } from '$lib/services/api/immutable';
import { UnsavedAssetProxy } from '$lib/services/api/unsaved-asset-proxy';
import { getAssetByPath, isAssetInFolder } from '$lib/services/assets';
import { getAssetFolder } from '$lib/services/assets/folders';
import { allAssets } from '$lib/services/assets/state';
import { getEntriesByCollection } from '$lib/services/contents/collection/entries';
import { getCollectionFileEntry } from '$lib/services/contents/collection/files';
import { getField } from '$lib/services/contents/entry/fields';
import { getOrCreate, memoizeOnSource } from '$lib/services/utils/cache';
import { unflattenMap } from '$lib/services/utils/object';

/**
 * @import { MapOf } from 'immutable';
 * @import {
 * Asset,
 * AssetFolderInfo,
 * Entry,
 * EntryDraft,
 * EntryFileMap,
 * FlattenedEntryContent,
 * GetFieldArgs,
 * InternalLocaleCode,
 * } from '$lib/types/private';
 * @import { ApiAsset, ApiEntry, FieldKeyPath } from '$lib/types/public';
 */

/**
 * @typedef {object} PreviewData
 * @property {MapOf<ApiEntry>} entryMap Immutable Map of entry data for the current locale.
 * @property {FlattenedEntryContent} valueMap Flattened content values for the current locale.
 * @property {Omit<GetFieldArgs, 'keyPath'>} getFieldArgs Arguments for getField function.
 * @property {MapOf<any>} fieldsMetaData Metadata for fields in the current locale.
 * @property {(path: string) => ApiAsset | undefined} getAsset Function to get asset URLs.
 */

/**
 * Create an Immutable.js Map representing the entry data, compatible with Netlify/Decap CMS event
 * hook handlers.
 * @param {object} args Arguments.
 * @param {FlattenedEntryContent} args.content Entry content for the default locale.
 * @param {string[]} args.otherLocales Other locale keys.
 * @param {Entry['locales']} args.locales All locale data keyed by locale.
 * @param {string} args.slug Entry slug.
 * @param {string} args.path Entry file path.
 * @param {boolean} args.isNew Whether the entry is new.
 * @param {string} args.collectionName Collection name.
 * @param {Asset[]} args.associatedAssets Assets associated with the entry.
 * @returns {MapOf<ApiEntry>} Immutable Map of the entry data.
 * @see https://immutable-js.com/docs/v5/Map/
 */
export const createEntryMap = ({
  content,
  otherLocales,
  locales,
  slug,
  path,
  isNew,
  collectionName,
  associatedAssets,
}) =>
  // @ts-ignore
  getImmutable().fromJS({
    // Entry data for the default locale
    data: unflattenMap(content),
    // Entry data for other locales
    // @see https://github.com/decaporg/decap-cms/issues/4729
    i18n: Object.fromEntries(
      otherLocales.map((locale) => [locale, { data: unflattenMap(locales[locale].content) }]),
    ),
    // Other entry properties
    slug,
    path,
    newRecord: isNew,
    collection: collectionName,
    mediaFiles: associatedAssets.map(({ sha, file, size, blobURL, ...asset }) => ({
      id: sha,
      name: asset.name,
      path: asset.path,
      file,
      size,
      url: blobURL,
      displayURL: blobURL,
    })),
    // Additional properties included for compatibility with Netlify/Decap CMS
    meta: { path },
    isModification: null,
    label: null,
    partial: false,
    author: '',
    raw: '',
    status: '',
    updatedOn: '',
  });

/**
 * Convert an entry to an Immutable Map for preview templates.
 * @param {object} args Arguments.
 * @param {Entry | undefined} args.entry Entry object to convert.
 * @param {InternalLocaleCode} args.locale Locale to use for content and path extraction.
 * @param {string} args.collectionName Collection name.
 * @param {Asset[]} args.associatedAssets Associated assets to include.
 * @param {FlattenedEntryContent} [args.content] Optional content override (if not provided,
 * extracted from entry).
 * @returns {MapOf<ApiEntry>} Immutable Map of entry data.
 */
export const convertEntryToMap = ({ entry, locale, collectionName, associatedAssets, content }) => {
  const entryContent = content ?? entry?.locales?.[locale]?.content ?? {};

  return /** @type {MapOf<ApiEntry>} */ (
    createEntryMap({
      content: entryContent,
      otherLocales: Object.keys(entry?.locales ?? {}).filter((l) => l !== locale),
      locales: entry?.locales ?? {},
      slug: entry?.slug ?? '',
      path: entry?.locales?.[locale]?.path ?? '',
      isNew: false,
      collectionName,
      associatedAssets,
    })
  );
};

/**
 * Create an asset getter function for React components (preview templates and custom field types).
 * @param {object} args Arguments.
 * @param {Entry} args.entry Entry object.
 * @param {string} args.collectionName Collection name.
 * @param {string} [args.fileName] File name.
 * @param {EntryFileMap} [args.files] Files added to the entry draft but not saved yet, keyed by the
 * blob URLs the field values refer to them with.
 * @returns {(path: string) => ApiAsset | undefined} Function that gets asset URLs.
 */
export const createGetAsset =
  ({ entry, collectionName, fileName, files }) =>
  /**
   * Get the asset URL for a given asset path.
   * @param {string} path Path to the asset, or the blob URL of a file that hasn’t been saved yet.
   * @returns {ApiAsset | undefined} Asset item.
   */
  (path) => {
    const unsavedFile = files?.[path]?.file;

    if (unsavedFile) {
      return new UnsavedAssetProxy(path, unsavedFile);
    }

    const asset = getAssetByPath({ value: path, entry, collectionName, fileName });

    if (asset) {
      return new AssetProxy(asset);
    }

    return undefined;
  };

/**
 * Cache of {@link getReferencedEntryLookup} results, keyed by the entry list they index.
 * @type {WeakMap<Entry[], Map<string, Map<any, Entry>>>}
 */
const referencedEntryLookupCache = new WeakMap();

/**
 * Get a lookup table of the given referenced entries by the value a Relation field stores. The
 * table is built once per entry list, locale and value field, instead of the list being scanned
 * for every value — the metadata is rebuilt whenever the entry draft is updated.
 * @param {Entry[]} entries Referenced entries.
 * @param {InternalLocaleCode} locale Locale code.
 * @param {string} valueField Relation field’s `value_field` option.
 * @returns {Map<any, Entry>} Entries by value. When several entries share a value, the first one
 * is kept.
 */
const getReferencedEntryLookup = (entries, locale, valueField) => {
  let lookups = referencedEntryLookupCache.get(entries);

  if (!lookups) {
    lookups = new Map();
    referencedEntryLookupCache.set(entries, lookups);
  }

  const key = `${locale}\n${valueField}`;
  const cached = lookups.get(key);

  if (cached) {
    return cached;
  }

  /** @type {Map<any, Entry>} */
  const lookup = new Map();

  entries.forEach((entry) => {
    const value =
      valueField === '{{slug}}' ? entry.slug : entry.locales[locale]?.content?.[valueField];

    if (!lookup.has(value)) {
      lookup.set(value, entry);
    }
  });

  lookups.set(key, lookup);

  return lookup;
};

/**
 * Get metadata for fields. For relation fields, looks up and stores the referenced entry content
 * keyed by collection name and value, matching the `fieldsMetaData` structure expected by
 * Netlify/Decap CMS preview templates and custom field types.
 * @param {object} args Arguments.
 * @param {InternalLocaleCode} args.locale Current locale.
 * @param {Omit<GetFieldArgs, 'keyPath'>} args.getFieldArgs Arguments for getField function.
 * @returns {import('immutable').MapOf<any>} Immutable Map of entry metadata.
 */
export const getMetaData = ({ locale, getFieldArgs }) => {
  const { valueMap = {} } = getFieldArgs;
  /** @type {Record<string, any>} */
  const metaData = {};
  /** @type {Map<string, Entry[]>} */
  const refEntriesCache = new Map();

  Object.entries(valueMap).forEach(([key, value]) => {
    const keyPath = /** @type {FieldKeyPath} */ (key.replace(/\.\d+$/, ''));
    const field = getField({ ...getFieldArgs, keyPath });

    // Populate metadata for relation fields by looking up referenced entries
    if (field?.widget === 'relation') {
      const {
        value_field: valueField = '{{slug}}',
        collection: refCollection,
        file: refFile,
      } = field;

      const refEntries = (() => {
        const cacheKey = `${refCollection}:${refFile ?? ''}`;
        const cache = refEntriesCache.get(cacheKey);

        if (cache) {
          return cache;
        }

        // The collection’s entry list is used as is, rather than copied, so that it keeps the same
        // identity across calls and its lookup table in `getReferencedEntryLookup()` can be reused
        const entries = refFile
          ? [getCollectionFileEntry(refCollection, refFile)].filter((entry) => !!entry)
          : getEntriesByCollection(refCollection);

        refEntriesCache.set(cacheKey, entries);

        return entries;
      })();

      metaData[keyPath] ??= {};
      metaData[keyPath][refCollection] ??= {};
      metaData[keyPath][refCollection][value] = getReferencedEntryLookup(
        refEntries,
        locale,
        valueField,
      ).get(value)?.locales[locale]?.content;
    }
  });

  return /** @type {import('immutable').MapOf<any>} */ (getImmutable().fromJS(metaData));
};

/**
 * Build a synthetic entry object with current values for live preview updates.
 * @param {object} args Arguments.
 * @param {Entry | undefined} args.originalEntry The original entry object.
 * @param {Record<InternalLocaleCode, FlattenedEntryContent>} args.currentValues Object with locale
 * keys mapping to current content values.
 * @returns {Entry} A new entry object with updated locale content while preserving original slugs
 * and paths.
 */
export const buildEntry = ({ originalEntry, currentValues }) =>
  /** @type {Entry} */ ({
    ...originalEntry,
    locales: Object.fromEntries(
      Object.entries(currentValues).map(([locale, content]) => [
        locale,
        {
          slug: originalEntry?.locales[locale]?.slug ?? originalEntry?.slug,
          path: originalEntry?.locales[locale]?.path ?? originalEntry?.subPath,
          content,
        },
      ]),
    ),
  });

/**
 * Cache of {@link getAssociatedPreviewAssets} results, keyed by the asset folder and dropped when
 * `allAssets` is replaced. The preview data is rebuilt whenever the entry draft is updated, so
 * without it every keystroke would walk the whole asset library.
 */
const getPreviewAssetCache = memoizeOnSource(
  () => allAssets.current,
  () => /** @type {WeakMap<AssetFolderInfo, Asset[]>} */ (new WeakMap()),
);

/**
 * Get assets associated with a collection or entry folder.
 * @param {object} args Arguments.
 * @param {string} [args.collectionName] Collection name.
 * @param {string} [args.fileName] File name.
 * @returns {Asset[]} Assets filtered to the relevant folder.
 */
export const getAssociatedPreviewAssets = ({ collectionName, fileName }) => {
  const assetFolder = getAssetFolder({ collectionName, fileName });

  if (!assetFolder) {
    return [];
  }

  return getOrCreate(getPreviewAssetCache(), assetFolder, () =>
    allAssets.current.filter((asset) => isAssetInFolder(asset, assetFolder)),
  );
};

/**
 * Build shared preview data used by both preview templates and custom field types.
 *
 * {@link PreviewData.fieldsMetaData} is computed only when it’s read, and the result is kept for
 * subsequent reads. Building it walks every value in the entry and scans the referenced collection
 * for each Relation field value, while a custom field control only needs
 * {@link PreviewData.entryMap}. Given that the data is rebuilt whenever the draft is updated, doing
 * that work upfront would cost a collection scan on every keystroke.
 * @param {object} args Arguments.
 * @param {EntryDraft} args.draft Entry draft being previewed.
 * @param {InternalLocaleCode} args.locale Current locale.
 * @returns {PreviewData} Object containing computed preview data.
 */
export const buildPreviewData = ({ draft, locale }) => {
  const { collectionName, fileName, isIndexFile, originalEntry, currentValues, files } = draft;
  const entry = buildEntry({ originalEntry, currentValues });
  /* v8 ignore next */
  const valueMap = entry.locales[locale].content ?? {};
  /** @type {Omit<GetFieldArgs, 'keyPath'>} */
  const getFieldArgs = { collectionName, fileName, valueMap, isIndexFile };
  /** @type {MapOf<any> | undefined} */
  let fieldsMetaData;

  return {
    entryMap: convertEntryToMap({
      entry,
      locale,
      collectionName,
      associatedAssets: getAssociatedPreviewAssets({ collectionName, fileName }),
      content: valueMap,
    }),
    valueMap,
    getFieldArgs,
    // eslint-disable-next-line jsdoc/require-jsdoc
    get fieldsMetaData() {
      fieldsMetaData ??= getMetaData({ locale, getFieldArgs });

      return fieldsMetaData;
    },
    getAsset: createGetAsset({ entry, collectionName, fileName, files }),
  };
};
